<template>
  <v-container fluid fill-height class="ma-0 pa-0">
    <v-row no-gutters>
      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn icon v-on="on" @click="selectChange">
            <v-icon>{{ selectIcon }}</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Select") }}</span>
      </v-tooltip>

      <div v-if="isInWorkflow">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="showFlowDiagram">
              <v-icon>mdi-state-machine</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("FlowChart") }}</span>
        </v-tooltip>
      </div>

      <div v-if="permissions.commentRead">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn @click="actionClick('comment')" icon v-on="on">
              <v-badge :color="badgeColor(commentsCount)" overlap bordered>
                <template v-slot:badge>
                  <span>{{ commentsCount }}</span>
                </template>

                <v-icon>mdi-message-reply-text</v-icon>
              </v-badge>
            </v-btn>
          </template>
          <span>{{ $t("Comments") }}</span>
        </v-tooltip>
      </div>

      <div v-if="permissions.documentAttachmentRead">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn @click="actionClick('attachment')" icon v-on="on">
              <v-badge :color="badgeColor(attachmentsCount)" overlap bordered>
                <template v-slot:badge>
                  <span>{{ attachmentsCount }}</span>
                </template>

                <v-icon>mdi-attachment</v-icon>
              </v-badge>
            </v-btn>
          </template>
          <span>{{ $t("DocumentAttachments") }}</span>
        </v-tooltip>
      </div>

      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn icon v-on="on" @click="actionClick('version')">
            <v-icon>mdi-book</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Versions") }}</span>
      </v-tooltip>

      <div v-if="permissions.auditRead">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn @click="actionClick('history')" icon v-on="on">
              <v-icon>mdi-backup-restore</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("History") }}</span>
        </v-tooltip>
      </div>

      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn @click="actionClick('alert')" icon v-on="on">
            <v-badge :color="badgeColor(alertsCount)" overlap>
              <template v-slot:badge>
                <span>{{ alertsCount }}</span>
              </template>

              <v-icon>mdi-bell</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span>{{ $t("Alerts") }}</span>
      </v-tooltip>

      <div v-if="isDistributed">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="actionClick('distribution')">
              <v-icon>mdi-share-variant</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("DistributionInternalExternal") }}</span>
        </v-tooltip>
      </div>

      <div v-if="hasPlugins">
        <v-menu bottom offset-y>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip top z-index="1000">
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                  @click="loadPluginList()"
                >
                  <v-icon>mdi-power-plug</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("Plugins") }}</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item-group>
              <v-list-item
                v-for="plugin in pluginList"
                v-bind:key="plugin.id"
                @click="loadPlugin(plugin)"
              >
                <v-list-item-icon>
                  <v-icon>{{ plugin.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ plugin.name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-menu>
      </div>

      <v-spacer />

      <div v-if="isPendingConfirmation">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="confirmReception">
              <v-icon>mdi-file-eye-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("ConfirmReception") }}</span>
        </v-tooltip>
      </div>

      <v-tooltip top z-index="1000" v-if="canUnblock">
        <template v-slot:activator="{ on }">
          <v-btn icon v-on="on" @click="unblockDocument">
            <v-icon>mdi-lock-open</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Unblock") }}</span>
      </v-tooltip>

      <v-tooltip top z-index="1000" v-if="isBlocked && !canUnblock">
        <template v-slot:activator="{ on }">
          <v-icon v-on="on">mdi-lock</v-icon>
        </template>
        <span>{{ lockInfo }}</span>
      </v-tooltip>

      <div v-if="permissions.documentEdit && isLastVersion">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="editDocument">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Edit") }}</span>
        </v-tooltip>
      </div>

      <div v-if="permissions.documentDelete">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="deleteDocument">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Delete") }}</span>
        </v-tooltip>
      </div>

      <div v-if="permissions.documentDownload">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="downloadDocument">
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Download") }}</span>
        </v-tooltip>
      </div>

      <div v-if="permissions.documentPrint">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="printDocument">
              <v-icon>mdi-printer</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Print") }}</span>
        </v-tooltip>
      </div>

      <div v-if="permissions.documentSign && isPdf && isLastVersion">
        <v-tooltip top z-index="1000">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="signDocument">
              <v-icon>mdi-draw</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("Sign") }}</span>
        </v-tooltip>
      </div>
      <v-tooltip top z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn icon v-on="on" @click="actionClick('expand')">
            <v-icon>{{ expandIcon }}</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Expand") }}</span>
      </v-tooltip>
    </v-row>
  </v-container>
</template>

<script>
import DocumentService from "@/services/DocumentService";
import PluginService from "@/services/PluginService";
import Plugin from "@/ecm/models/plugin/Plugin";
import Utils from "@/ecm/common/Utils.js";
import { EventBus } from "@/eventBus.js";
import AccountType from "@/ecm/variables/AccountType";

export default {
  name: "DocumentItemToolbar",
  props: {
    documentId: {
      type: Number,
    },
    commentsCount: {
      type: Number,
      default: 0,
    },
    alertsCount: {
      type: Number,
      default: 0,
    },
    attachmentsCount: {
      type: Number,
      default: 0,
    },
    canUnblock: {
      type: Boolean,
      default: false,
    },
    isBlocked: {
      type: Boolean,
      default: false,
    },
    lockInfo: {
      type: String,
      default: "",
    },
    isDistributed: {
      type: Boolean,
      default: false,
    },
    isPendingConfirmation: {
      type: Boolean,
      default: false,
    },
    permissions: {
      type: Object,
      required: true,
    },
    isLastVersion: {
      type: Boolean,
      default: false,
    },
    isInWorkflow: {
      type: Boolean,
      default: false,
    },
    hasPlugins: {
      type: Boolean,
      default: false,
    },
    isPdf: {
      type: Boolean,
      default: false,
    },
    selectedDocuments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      pluginList: [],
      expandIcon: "mdi-chevron-down",
    };
  },
  computed: {
    isSelected() {
      return (
        this.selectedDocuments.filter((d) => d == this.documentId).length > 0
      );
    },
    selectIcon() {
      return this.isSelected
        ? "mdi-check-box-outline"
        : "mdi-checkbox-blank-outline";
    },
    isReadonlyUser() {
      return (
        this.$store.getters["account/user"].AccountType == AccountType.Readonly
      );
    },
  },
  methods: {
    actionClick(action) {
      this.$emit("on-action-click", action);
      if (action === "expand") {
        this.expandIcon =
          this.expandIcon == "mdi-chevron-down"
            ? "mdi-chevron-up"
            : "mdi-chevron-down";
      } else {
        this.expandIcon = "mdi-chevron-down";
      }
    },
    selectChange() {
      this.$emit("on-selection-change", !this.isSelected);
    },
    deleteDocument() {
      const ids = [this.documentId];
      this.$confirm(this.$t("DeleteAllVersionsConfirmation"), {
        title: this.$t("Delete"),
        icon: "mdi-lock-question",
        color: "warning",
        width: 400,
        buttonTrueText: this.$t("CurrentVersion"),
        buttonFalseText: this.$t("AllVersions"),
      }).then((res) => {
        if (res > 0) {
          var deleteAllVersions = res != 1;
          DocumentService.removeVersion(ids, deleteAllVersions).then(
            (result) => {
              Utils.toast.success(this.$t("DocumentsDeletedSuccessfully"));
              if (deleteAllVersions) {
                this.$emit("on-document-removed", this.documentId);
              } else {
                this.$emit("on-version-removed", result);
              }
            }
          );
        }
      });
    },
    downloadDocument() {
      const id = this.documentId;
      if (this.isReadonlyUser) {
        DocumentService.download(id, false);
        return;
      }

      if (this.isBlocked && !this.canUnblock) {
        DocumentService.download(id, false);
        return;
      }
      this.$confirm(this.$t("LockForEditionConfirmation"), {
        title: this.$t("Download"),
        icon: "mdi-lock-question",
        color: "warning",
        width: 400,
        buttonTrueText: this.$t("DownloadAndBlock"),
        buttonFalseText: this.$t("Download"),
      }).then((res) => {
        if (res > 0) {
          let blockDocument = res == 1;
          if (blockDocument && !this.permissions.documentEdit) {
            Utils.toast.error(this.$t("CannotEditAndDownload"));
            return;
          } else {
            DocumentService.download(id, blockDocument);

            if (res == 1) this.$emit("on-document-blocked");
          }
        }
      });
    },
    unblockDocument() {
      const ids = [this.documentId];
      DocumentService.unlock(ids).then((result) => {
        this.$emit("on-document-blocked");
      });
    },
    editDocument() {
      this.$emit("on-edit-click");
    },
    showFlowDiagram() {
      this.$emit("on-flow-diagram-click");
    },
    signDocument() {
      this.$emit("on-sign-click");
    },
    confirmReception() {
      DocumentService.confirmReception(this.documentId).then((result) => {
        this.$emit("on-reception-confirmed");
      });
    },
    printDocument() {
      DocumentService.print(this.documentId);
    },
    loadPlugin(plugin) {
      this.$emit("on-load-plugin-click", plugin);
    },
    loadPluginList() {
      PluginService.getAuthorizedList("ResultViewDocument", [
        this.documentId,
      ]).then((result) => {
        this.pluginList = result.map(function (dto) {
          return new Plugin(dto);
        });
      });
    },
    badgeColor(count) {
      return count > 0 ? "red lighten-1" : "grey lighten-1";
    },
  },
};
</script>
